import { InputAdornment, SxProps, TextField } from "@mui/material";
import { ReactElement } from "react";
import { MAIN_FONT_FAMILY } from "../../data/constant/Constants";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../data/colors/Colors";
import { Theme } from "@emotion/react";

interface props {
  type?: React.HTMLInputTypeAttribute;
  placeholder: string;
  variant?: "outlined" | "standard";
  onTextChange: (text: string) => void;
  isFullWidth?: boolean;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  borderRadius?: number;
  textColor?: string;
  backgroundColor?: string;
  onHoverBorderColor?: string;
  borderColor?: string;
  onFocusBorderColor?: string;
  width?: number;
  height?: number;
  error?: boolean;
  helperText?: string;
  sxProps?: SxProps<Theme>;
  fontSize?: number;
  fontWeight?: string;
  value?: string;
  multiLine?: boolean;
  required?: boolean;
}

export const CustomTextField: React.FC<props> = ({
  type = "text",
  variant = "outlined",
  placeholder,
  onTextChange,
  isFullWidth = true,
  startIcon,
  endIcon,
  value,
  multiLine = false,
  borderRadius = 12,
  textColor = COLOR_BLACK,
  backgroundColor = "transparent",
  onHoverBorderColor = COLOR_MAIN,
  borderColor = COLOR_LIGHT_GRAY,
  onFocusBorderColor = COLOR_MAIN,
  width,
  height,
  error,
  helperText,
  sxProps,
  fontSize,
  fontWeight,
  required,
}: props) => {
  return (
    <TextField
      type={type}
      value={value}
      placeholder={placeholder}
      variant={variant}
      onChange={(txt) => onTextChange(txt.currentTarget.value)}
      autoComplete="off"
      fullWidth={isFullWidth}
      multiline={multiLine}
      error={error}
      helperText={helperText}
      required={required}
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ),
        endAdornment: <InputAdornment position="end">{endIcon}</InputAdornment>,
        sx: {
          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
            {
              display: "none",
            },
          "& input[type=number]": {
            MozAppearance: "textfield", // For Firefox
          },
          borderRadius: borderRadius,
          fontFamily: MAIN_FONT_FAMILY,
          color: textColor,
          background: backgroundColor,
          width,
          height,
          fontWeight,
          fontSize,
        },
      }}
      sx={[
        {
          "& .MuiOutlinedInput-root": {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: onHoverBorderColor,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor,
            },
            "&.Mui-focused fieldset": {
              borderColor: onFocusBorderColor,
              borderWidth: 1,
            },
          },
        },
        ...(Array.isArray(sxProps) ? sxProps : [sxProps]),
      ]}
    />
  );
};
